import { useCallback } from 'react';

import { boardSwitcherSharedState } from '@trello/board-switcher';
import type { ShortcutHandler } from '@trello/keybindings';
import { Key, Scope, useShortcutHandler } from '@trello/keybindings';
import { useSharedState, useSharedStateSelector } from '@trello/shared-state';

import type { BoardSwitcherSection } from './BoardSwitcher.types';
import { getNthSiblingNavItem } from './getNavItemTreeWalker';
import { keyboardNavSharedState } from './keyboardNavSharedState';

// List of keys that should not trigger re-focusing the search input
const IGNORED_KEYS: string[] = [
  Key.Enter,
  Key.Escape,
  Key.Alt,
  Key.Control,
  Key.Meta,
  Key.Shift,
  Key.Space,
  Key.Tab,
];

interface FocusFunctionArgs {
  activeSection: BoardSwitcherSection | null;
  activeId: string | null;
  skip: number;
  searchElement?: HTMLInputElement | null;
}

const focusNextNavItem = ({
  activeSection,
  activeId,
  skip,
}: FocusFunctionArgs) => {
  const navItem = getNthSiblingNavItem(activeSection, activeId, skip, 'next');

  if (navItem) {
    keyboardNavSharedState.setValue(navItem);
  }
};

const focusPreviousNavItemOrSearchInput = ({
  activeSection,
  activeId,
  skip,
  searchElement,
}: FocusFunctionArgs) => {
  const navItem = getNthSiblingNavItem(
    activeSection,
    activeId,
    skip,
    'previous',
  );

  if (navItem) {
    keyboardNavSharedState.setValue(navItem);
    return;
  }

  if (searchElement) {
    searchElement.focus();
  }
};

export const useBoardSwitcherKeyboardNav = (
  searchElement: HTMLInputElement | null,
) => {
  const [{ activeSection, activeId }] = useSharedState(keyboardNavSharedState);
  const layout = useSharedStateSelector(
    boardSwitcherSharedState,
    useCallback((value) => value.layout, []),
  );

  const handleKeyboardNavigation = useCallback<ShortcutHandler>(
    (e) => {
      // TODO: dynamically determine the number of siblings to skip based on
      // the number of columns in the board switcher.

      switch (e.key) {
        case Key.ArrowRight:
          e.preventDefault();
          focusNextNavItem({ activeSection, activeId, skip: 1 });
          return;
        case Key.ArrowDown:
          e.preventDefault();
          focusNextNavItem({
            activeSection,
            activeId,
            skip: layout === 'grid' ? 4 : 1,
          });
          return;
        case Key.ArrowLeft:
          e.preventDefault();
          focusPreviousNavItemOrSearchInput({
            activeSection,
            activeId,
            skip: 1,
            searchElement,
          });
          return;
        case Key.ArrowUp:
          e.preventDefault();
          focusPreviousNavItemOrSearchInput({
            activeSection,
            activeId,
            skip: layout === 'grid' ? 4 : 1,
            searchElement,
          });
          return;
        default:
          break;
      }
      if (!IGNORED_KEYS.includes(e.key)) {
        searchElement?.focus();
      }
    },
    [activeSection, activeId, searchElement, layout],
  );

  useShortcutHandler(handleKeyboardNavigation, {
    scope: Scope.Overlay,
  });

  return null;
};
